import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

interface RatesExpansionContextValue {
  expandedRateId: string | null;
  setExpandedRateId: (id: string | null) => void;
  toggleExpanded: (id: string) => void;
}

const RatesExpansionContext = createContext<RatesExpansionContextValue | null>(null);

interface RatesExpansionProviderProps extends PropsWithChildren {
  initialExpandedRateId?: string | null;
}

export const RatesExpansionProvider: FC<RatesExpansionProviderProps> = ({ initialExpandedRateId = null, children }) => {
  const [expandedRateId, setExpandedRateId] = useState<string | null>(initialExpandedRateId);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedRateId((prev) => (prev === id ? null : id));
  }, []);

  const value = useMemo<RatesExpansionContextValue>(
    () => ({ expandedRateId, setExpandedRateId, toggleExpanded }),
    [expandedRateId, toggleExpanded]
  );

  return <RatesExpansionContext.Provider value={value}>{children}</RatesExpansionContext.Provider>;
};

export const ControlledRatesExpansionProvider: FC<PropsWithChildren<RatesExpansionContextValue>> = ({
  expandedRateId,
  setExpandedRateId,
  toggleExpanded,
  children,
}) => {
  const value = useMemo<RatesExpansionContextValue>(
    () => ({ expandedRateId, setExpandedRateId, toggleExpanded }),
    [expandedRateId, setExpandedRateId, toggleExpanded]
  );
  return <RatesExpansionContext.Provider value={value}>{children}</RatesExpansionContext.Provider>;
};

export const useRatesExpansion = (): RatesExpansionContextValue => {
  const ctx = useContext(RatesExpansionContext);
  if (!ctx) {
    throw new Error("useRatesExpansion must be used within a RatesExpansionProvider");
  }
  return ctx;
};
